import {Button} from '@wandb/components';
// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon} from '@wandb/weave/components/Icon';
import React, {useState} from 'react';

import {RoleDropdown} from './MembersTable/AccountSettingsMembersTable/RoleDropdown';
import {RoleOption} from './MembersTable/TeamOrOrganizationRoleOption';
import {TeamOption, TeamSelect} from './TeamSelect';
import {UsersToInviteInput} from './UsersToInviteInput';

type BulkInviteFormData = {
  users: string[];
  teams: string[];
  role: string;
};

type BulkInviteFormProps = {
  onSubmit: (formData: BulkInviteFormData) => void;
  teamOptions: TeamOption[];
  roleOptions: RoleOption[];
  availableSeats: number;
};

export const BulkInviteForm: React.FC<BulkInviteFormProps> = ({
  onSubmit,
  teamOptions,
  roleOptions,
  availableSeats,
}) => {
  const [users, setUsers] = useState<string[]>([]);
  const [teams, setTeams] = useState<string[]>([]);
  const [hasInvalidEmails, setHasInvalidEmails] = useState<boolean>(false);
  const [role, setRole] = useState<string>(roleOptions[0]?.value ?? '');

  const handleSubmit = () => {
    onSubmit({users, teams, role});
  };

  // Form is valid if:
  // 1. At least one user is selected
  // 2. At least one team is selected
  // 3. No invalid emails exist
  // 4. A role is selected
  const isFormValid =
    users.length > 0 && teams.length > 0 && !hasInvalidEmails && role !== '';

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto mb-10 mt-10">
        <div className="mb-20 px-3">
          <div className="block font-medium font-semibold mb-2">
            Email(s) or Username(s)
          </div>
          <UsersToInviteInput
            value={users}
            onChange={setUsers}
            onValidationChange={setHasInvalidEmails}
            maxUsers={10}
            placeholder="Enter email(s) or username(s), separated by commas."
            usernameInviteEnabled={true}
          />
        </div>

        <div className="mb-20 px-3">
          <div className="block font-medium font-semibold mb-2">Team(s)</div>
          <TeamSelect
            teamOptions={teamOptions}
            teams={teams}
            setTeams={setTeams}
          />
        </div>

        <div className="mb-30 px-3">
          <label className="mb-1 block font-semibold">{'Select role'}</label>
          <RoleDropdown
            role={role}
            roleOptions={roleOptions}
            setRole={setRole}
          />
        </div>

        <div className="bg-moon-50 rounded-lg p-12 border border-moon-300">
          <div className="flex items-center mb-8 gap-8">
            <Icon name="checkmark-circle" />
            <span className="text-md text-moon-650">
              Default product access
            </span>
          </div>

          <div className="ml-30 mb-20">
            <div className="flex items-center gap-6 mb-4">
              <Icon name="checkmark" />
              <span className="text-md text-moon-650">
                Models - Full access if seats remain available ({availableSeats}{' '}
                seats remaining)
              </span>
            </div>
            <div className="flex items-center gap-6">
              <Icon name="checkmark" />
              <span className="text-md text-moon-650">
                Weave - Full access (Unlimited seats)
              </span>
            </div>
          </div>

          <div className="flex gap-8">
            <Icon name="info" />
            <span className="text-md text-moon-500">
              If Model seats fill before acceptance, users will still be able to
              join and can be given access later.
            </span>
          </div>
        </div>
      </div>

      <div className="flex-shrink-0">
        <Button
          fullWidth
          data-test="add-member-button"
          variant="primary"
          size="md"
          onPress={handleSubmit}
          isDisabled={!isFormValid}>
          Invite user(s)
        </Button>
      </div>
    </div>
  );
};
